import { useAtomValue } from "jotai";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useWorkspace from "./useWorkspace";
import useTenantUsers from "../tenantUsers/useTenantUsers";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import useUser from "../auth/useUser";
import WorkspaceAPI from "../../api/WorkspaceAPI";
import { handleWorkspacesUserUpdate } from "../../../utils/WorkspaceUtils";
import {
  callFunctionIfExists,
  getErrorMessage,
} from "../../../utils/GeneralUtils";

const useTransferWorkspaceOwnership = () => {
  const { user, wPermissions: workspacePermissions } = useUser();
  const { workspace } = useWorkspace();
  const queryClient = useQueryClient();
  const tenantID = useAtomValue(tenantIDAtom);

  const { users } = useTenantUsers();

  const transferOwnership = useMutation({
    mutationFn: ({
      newUserID,
      permissionProfile,
    }: {
      newUserID: string;
      permissionProfile: any;
      onSuccess?: () => void;
    }) => {
      if (!workspace) {
        return Promise.reject("No workspace found");
      }
      if (workspace.Owner !== user.id) {
        return Promise.reject("Only the owner can transfer ownership");
      }

      return WorkspaceAPI.transferWorkspaceOwnership(
        workspace.id,
        newUserID,
        permissionProfile
      );
    },
    onSuccess: (data, { onSuccess }) => {
      handleWorkspacesUserUpdate(
        data,
        queryClient,
        workspace.id || "",
        workspacePermissions,
        users,
        tenantID || ""
      );

      callFunctionIfExists(onSuccess);
    },
    onError: (error) => {
      console.error("Error transferring workspace ownership:", error);
    },
  });

  return {
    transferOwnership: transferOwnership.mutate,
    transferringOwnership: transferOwnership.isPending,
    transferOwnershipError: transferOwnership.error
      ? getErrorMessage(transferOwnership.error)
      : null,
  };
};

export default useTransferWorkspaceOwnership;
